import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ShieldCheck, Languages, Menu, X } from "lucide-react";
import { useI18n } from "../i18n";

export const SiteHeader = () => {
  const { t, lang, setLang } = useI18n();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const onStudio = pathname.startsWith("/studio");

  const links = [
    ["#how", "nav.how", "nav-how"],
    ["#formats", "nav.formats", "nav-formats"],
    ["#faq", "nav.faq", "nav-faq"],
  ];

  return (
    <header className="sticky top-0 z-50 bg-black/70 backdrop-blur-xl backdrop-saturate-150 border-b border-white/10">
      <div className="mx-auto max-w-7xl px-5 h-14 flex items-center gap-6">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-2 group">
          <ShieldCheck className="h-5 w-5 text-sky-400 group-hover:text-sky-300 transition-colors" strokeWidth={1.75} />
          <span className="font-head text-[15px] font-semibold tracking-tight">
            AIwatermark<span className="text-sky-400">.studio</span>
          </span>
        </Link>

        {!onStudio && (
          <nav className="hidden md:flex items-center gap-6 text-sm text-slate-400">
            {links.map(([href, key, tid]) => (
              <a key={key} href={href} data-testid={tid} className="hover:text-white transition-colors">{t(key)}</a>
            ))}
          </nav>
        )}

        <div className="ml-auto flex items-center gap-2 sm:gap-3">
          <div className="flex items-center rounded-full border border-white/10 bg-white/5 p-0.5" data-testid="lang-switch">
            <Languages className="h-3.5 w-3.5 text-slate-500 mx-1.5" strokeWidth={1.75} />
            {["en", "fr"].map((code) => (
              <button
                key={code}
                data-testid={`lang-${code}`}
                onClick={() => setLang(code)}
                className={`px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider rounded-full transition-colors active:scale-[0.97] ${
                  lang === code ? "bg-sky-500 text-white" : "text-slate-400 hover:text-white"
                }`}
              >
                {code}
              </button>
            ))}
          </div>
          <Link
            to={onStudio ? "/" : "/studio"}
            data-testid={onStudio ? "header-home-btn" : "header-studio-btn"}
            className="hidden sm:inline-flex items-center rounded-full bg-white/10 hover:bg-white/20 border border-white/10 px-4 py-1.5 text-[13px] font-medium transition-colors active:scale-[0.98]"
          >
            {onStudio ? t("nav.back") : t("nav.open")}
          </Link>
          <button
            data-testid="mobile-menu-btn"
            aria-label="menu"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            className="inline-flex items-center justify-center rounded-full border border-white/10 bg-white/5 p-2 text-slate-300 transition-colors hover:bg-white/10 hover:text-white sm:hidden"
          >
            {open ? <X className="h-4 w-4" strokeWidth={2} /> : <Menu className="h-4 w-4" strokeWidth={2} />}
          </button>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="sm:hidden border-t border-white/10 bg-black/95 px-5 py-4">
          <nav className="flex flex-col gap-1">
            {!onStudio &&
              links.map(([href, key, tid]) => (
                <a
                  key={key}
                  href={href}
                  data-testid={`m-${tid}`}
                  onClick={() => setOpen(false)}
                  className="rounded-lg px-3 py-2.5 text-sm text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
                >
                  {t(key)}
                </a>
              ))}
            <Link
              to={onStudio ? "/" : "/studio"}
              data-testid="mobile-studio-btn"
              onClick={() => setOpen(false)}
              className="mt-2 rounded-full bg-sky-500 px-4 py-2.5 text-center text-sm font-medium text-white transition-colors hover:bg-sky-400"
            >
              {onStudio ? t("nav.back") : t("nav.open")}
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
};
